import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import * as auth from "../../store/ducks/auth.duck";
import {
  setupMfaRecovery,
  completeMfaRecovery,
  me,
} from "../../crud/auth.crud";

const STEPS = {
  LOADING: "LOADING",
  SETUP: "SETUP",
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
};

function MfaRecoveryPage(props) {
  const [step, setStep] = useState(STEPS.LOADING);
  const [error, setError] = useState("");
  const [token, setToken] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get("token");

    if (!tokenParam) {
      setError("Token de recuperação não encontrado.");
      setStep(STEPS.ERROR);
      return;
    }

    setToken(tokenParam);
    initSetup(tokenParam);
  }, [location]);

  const initSetup = async (recoveryToken) => {
    try {
      const response = await setupMfaRecovery(recoveryToken);
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
      setStep(STEPS.SETUP);
    } catch (err) {
      handleError(err);
    }
  };

  const handleError = (err) => {
    const message = err.response?.data?.message;
    const errorMessage =
      (Array.isArray(message) ? message[0] : message) ||
      err.message ||
      "Erro desconhecido.";

    setError(errorMessage || "Erro ao processar recuperação. Tente novamente.");
    setStep(STEPS.ERROR);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6 || submitting) return;

    setSubmitting(true);
    try {
      const response = await completeMfaRecovery(token, code);
      setBackupCodes(response.data.backup_codes);
      setStep(STEPS.SUCCESS);

      const meResponse = await me();
      const user = meResponse.data;

      if (user.identity?.ctx !== "barbeiro") {
        setError("Contexto inválido para este painel.");
        setStep(STEPS.ERROR);
        return;
      }

      props.login({ user, authToken: null });
    } catch (err) {
      if (
        err.response?.status === 400 &&
        err.response?.data?.message?.includes("código")
      ) {
        setError("Código inválido. Verifique e tente novamente.");
        setCode("");
        setSubmitting(false);
        return;
      }

      handleError(err);
    }
  };

  const handleGoToLogin = () => {
    history.push("/auth/login");
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(value);
  };

  if (step === STEPS.LOADING) {
    return (
      <div className="kt-login__body">
        <div className="kt-login__form">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Processando...</span>
            </div>
            <p className="mt-3 text-muted">
              Preparando recuperação de acesso...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (step === STEPS.ERROR) {
    return (
      <div className="kt-login__body">
        <div className="kt-login__form">
          <div className="kt-login__title">
            <h3>Recuperação de MFA</h3>
          </div>

          <div className="alert alert-danger">{error}</div>

          <button
            type="button"
            onClick={handleGoToLogin}
            className="btn btn-secondary btn-elevate btn-block"
          >
            Voltar ao login
          </button>
        </div>
      </div>
    );
  }

  if (step === STEPS.SETUP) {
    return (
      <div className="kt-login__body">
        <div className="kt-login__form">
          <div className="kt-login__title">
            <h3>Configurar novo MFA</h3>
          </div>

          <p className="text-muted text-center mb-4">
            Escaneie o QR Code com seu aplicativo autenticador
          </p>

          <div className="text-center mb-4">
            <img
              src={qrCode}
              alt="QR Code para autenticação"
              style={{
                width: "200px",
                height: "200px",
                border: "1px solid #eee",
                borderRadius: "8px",
              }}
            />
          </div>

          <div
            style={{
              padding: "12px",
              backgroundColor: "#f8f9fa",
              borderRadius: "6px",
              marginBottom: "20px",
            }}
          >
            <p
              className="text-muted text-center mb-1"
              style={{ fontSize: "12px" }}
            >
              Código manual:
            </p>
            <code
              style={{
                display: "block",
                textAlign: "center",
                fontSize: "14px",
                letterSpacing: "2px",
                wordBreak: "break-all",
              }}
            >
              {secret}
            </code>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Código de verificação</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                className="form-control text-center"
                placeholder="000000"
                value={code}
                onChange={handleCodeChange}
                maxLength={6}
                style={{ fontSize: "24px", letterSpacing: "8px" }}
                disabled={submitting}
                autoFocus
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-elevate btn-block"
              disabled={code.length !== 6 || submitting}
            >
              {submitting ? "Verificando..." : "Confirmar"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (step === STEPS.SUCCESS) {
    return (
      <div className="kt-login__body">
        <div className="kt-login__form">
          <div className="kt-login__title">
            <h3>Configurado!</h3>
          </div>

          <div
            style={{
              padding: "20px",
              backgroundColor: "#d4edda",
              borderRadius: "6px",
              border: "1px solid #c3e6cb",
              marginBottom: "20px",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#155724",
                textAlign: "center",
              }}
            >
              ✓ Autenticação de dois fatores reconfigurada com sucesso!
            </p>
          </div>

          <div
            style={{
              padding: "16px",
              backgroundColor: "#fff3cd",
              borderRadius: "6px",
              border: "1px solid #ffc107",
              marginBottom: "20px",
            }}
          >
            <p
              style={{
                margin: "0 0 12px 0",
                fontSize: "14px",
                color: "#856404",
                fontWeight: 600,
              }}
            >
              Guarde seus códigos de backup:
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "8px",
                backgroundColor: "#fff",
                padding: "12px",
                borderRadius: "4px",
              }}
            >
              {backupCodes.map((backupCode) => (
                <code
                  key={backupCode}
                  style={{
                    fontSize: "14px",
                    textAlign: "center",
                    padding: "4px",
                  }}
                >
                  {backupCode}
                </code>
              ))}
            </div>
            <p
              style={{
                margin: "12px 0 0 0",
                fontSize: "12px",
                color: "#856404",
              }}
            >
              Estes códigos só serão exibidos uma vez. Guarde-os em um local
              seguro.
            </p>
          </div>

          <p className="text-muted text-center">
            Você será redirecionado automaticamente...
          </p>
        </div>
      </div>
    );
  }

  return null;
}

export default connect(null, auth.actions)(MfaRecoveryPage);
